import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { CopyRefChip } from 'components/ui/data-table';
import Skeleton from 'components/ui/skeleton';
import { StatusTabs, type StatusTabOption } from 'components/ui/status-tabs';
import * as CollectionsModule from 'modules/collections';
import * as TeamsModule from 'modules/teams';
import type { TeamDetail } from 'modules/teams/types';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { GroupTile } from './GroupTile';
import { groupStudents } from './groupRoster';

export type GroupTab = 'members' | 'collections' | 'materials' | 'settings';

/** Materials rides the base route via `?tab=materials`, since it has no dedicated gated route. */
const TAB_HREF: Record<GroupTab, (id: string) => string> = {
  members: (id) => `/app/teams/${id}`,
  collections: (id) => `/app/teams/${id}/collections`,
  materials: (id) => `/app/teams/${id}?tab=materials`,
  settings: (id) => `/app/teams/${id}/settings`,
};

/** Crumb, identity and tabs, then a toolbar and table block while the group loads. */
export const GroupProfileSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4" aria-hidden>
    <Skeleton className="h-4 w-40" />
    <div className="flex items-center gap-3.5">
      <Skeleton className="h-[52px] w-[52px] rounded-lg" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-3.5 w-72" />
      </div>
    </div>
    <Skeleton className="h-10 w-full max-w-xl" />
    <Skeleton className="h-9 w-72" />
    <Skeleton className="h-72 w-full rounded-lg" />
  </div>
);

export interface GroupProfileProps {
  team: TeamDetail;
  activeTab: GroupTab;
  /** Header trailing actions (invite, assign, share…). */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/** The group's identity, its actions and the tab row, shared by every group route. */
export const GroupProfile: React.FC<GroupProfileProps> = ({ team, activeTab, actions, children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  // The Settings route is gated on TEAMS_EDIT; don't offer a tab that bounces.
  const canEdit = usePermission(PERMISSIONS.TEAMS_EDIT);

  // Counts for the tab bar (cached queries; cheap to share here).
  const { collections, isLoading: collectionsLoading } = CollectionsModule.Hooks.useByTeam({
    teamId: team.id,
    enabled: true,
  });
  const { joinToken } = TeamsModule.Hooks.useJoinToken({ teamId: team.id, enabled: true });

  const studentCount = groupStudents(team).length;
  const teachers = team.members.filter((m) => m.role === 'admin');
  const iTeach = teachers.some((m) => m.userId === authState.user?.id);
  const coTeachers = teachers.length - (iTeach ? 1 : 0);

  const tabs: StatusTabOption<GroupTab>[] = [
    { value: 'members', label: t('group_tab_members'), count: studentCount },
    {
      value: 'collections',
      label: t('group_tab_collections'),
      count: collectionsLoading ? undefined : collections.length,
    },
    { value: 'materials', label: t('group_tab_materials') },
    ...(canEdit ? [{ value: 'settings' as const, label: t('group_tab_settings') }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* The shell crumb stops at "Guruhlar", so the page names the group. */}
      <nav aria-label={t('breadcrumb')} className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
        <Link
          to="/app/teams"
          className="shrink-0 rounded-sm transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('nav.groups')}
        </Link>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden />
        <span className="truncate" aria-current="page">
          {team.name}
        </span>
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3.5">
          <GroupTile name={team.name} className="h-[52px] w-[52px] text-base" />
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="truncate text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">{team.name}</h1>
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-muted-foreground">
              <span className="tabular-nums">{t('group_students_count', { count: studentCount })}</span>
              {iTeach && coTeachers > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span>{t('group_coteachers_meta', { count: coTeachers })}</span>
                </>
              )}
              {joinToken?.code && (
                <>
                  <span aria-hidden>·</span>
                  <span>{t('group_code_meta')}</span>
                  <CopyRefChip
                    value={joinToken.code}
                    label={t('copy_code')}
                    copiedLabel={t('group_code_copied')}
                    className="h-[22px] border border-border bg-card px-[7px] py-0 text-[11.5px] font-medium tracking-wide text-foreground/80 hover:bg-muted"
                  />
                </>
              )}
            </p>
          </div>
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>

      <StatusTabs
        aria-label={t('group_tabs')}
        options={tabs}
        value={activeTab}
        onChange={(tab) => navigate(TAB_HREF[tab](team.id))}
      />

      <div>{children}</div>
    </div>
  );
};

export default GroupProfile;
